import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { getPhotoUrl } from "@/utils/photoUrl";
import { User, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function NeedsEvaluationBanner({ onSelectPlayer, evaluationType }) {
  const [playerCounts, setPlayerCounts] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API}/evaluations/player-counts`);
        setPlayerCounts(res.data || []);
      } catch (error) {
        console.error("Erro ao carregar contagem de avaliações:", error);
      }
    };
    fetchCounts();
  }, []);

  // Filter by evaluation type
  const filtered = playerCounts.filter((p) => {
    if (evaluationType === "levantador") return p.position === "Levantador";
    if (evaluationType === "jogador") return p.position !== "Levantador";
    return true;
  });

  // Show top players with fewest evaluations
  const visibleCount = expanded ? filtered.length : 5;
  const visible = filtered.slice(0, visibleCount);

  if (visible.length === 0) return null;

  const getName = (p) => {
    if (p.first_name) return `${p.first_name} ${p.last_name || ""}`.trim();
    return p.name || "";
  };

  return (
    <div className="rounded-xl border-2 border-amber-500/30 bg-amber-500/5 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-amber-500/20 bg-amber-500/10">
        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
          Precisam de avaliação
        </span>
        <span className="text-xs text-muted-foreground ml-auto">
          (menos avaliados)
        </span>
      </div>
      <div className="divide-y divide-border/40">
        {visible.map((player) => (
          <button
            key={player.id}
            type="button"
            onClick={() => onSelectPlayer(player)}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-500/10 transition-colors text-left"
            data-testid={`needs-eval-${player.id}`}
          >
            <div className="w-9 h-9 rounded-full bg-muted overflow-hidden flex-shrink-0">
              {player.photo_url ? (
                <img
                  src={getPhotoUrl(player.photo_url)}
                  alt={getName(player)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{getName(player)}</p>
              <p className="text-xs text-muted-foreground">{player.position}</p>
            </div>
            <Badge
              variant={player.evaluation_count === 0 ? "destructive" : "secondary"}
              className="text-xs tabular-nums flex-shrink-0"
            >
              {player.evaluation_count === 0
                ? "Nenhuma"
                : `${player.evaluation_count} aval.`}
            </Badge>
          </button>
        ))}
      </div>
      {filtered.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors border-t border-border/40 bg-muted/20"
          data-testid="needs-eval-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Mostrar menos
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Ver todos ({filtered.length})
            </>
          )}
        </button>
      )}
    </div>
  );
}
